import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { Typography } from '@libs/components';
import { screenSizes } from '@libs/theme';

import { HistoryPeriodEnumKeys, WalletInfoDetailed } from 'src/gql';
import { useDimensions, useTranslation } from 'src/hooks';

import { PIE_CHART_PERCENTAGE_THRESHOLD } from '../pie-chart-data/PieChartData';
import PieChartDetails from '../pie-chart-details/PieChartDetails';
import { DonutChartData, PieChart } from '../pie-chart/PieChart';

import {
  BackButtonWrapper,
  CustomDropDownIcon,
  PieChartContainer,
  PieChartWrapper,
  TextContainer,
} from './styles';

type PieChartOtherDataProps = {
  totalValue: number;
  currencySymbol: string;
  data: WalletInfoDetailed[];
  activeDurationKey: HistoryPeriodEnumKeys;
  activeIndex: number | undefined;
  setActiveIndex: (index: number | undefined) => void;
  setOtherActiveIndex: (index: number | undefined) => void;
};
const PieChartOtherData: React.FC<PieChartOtherDataProps> = ({
  data,
  totalValue,
  currencySymbol,
  activeDurationKey,
  activeIndex,
  setActiveIndex,
  setOtherActiveIndex,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { width } = useDimensions();

  const titleType = width < screenSizes.laptop ? 'bodyButton' : 'subHeadline';

  const otherChartData: DonutChartData[] = useMemo(() => {
    if (data.length <= 0) return [];

    return data.map((item, index) => {
      const percentage = (item.value * 100) / totalValue;

      const percentageToApply = Math.max(
        percentage || 0,
        PIE_CHART_PERCENTAGE_THRESHOLD
      );

      return {
        key: item.code.toString(),
        value: percentageToApply,
        isActive: activeIndex === index,
        color: item.color || '',
      };
    });
  }, [data, activeIndex, totalValue]);

  const totalAssetCount = data.length;
  const selectedAsset =
    typeof activeIndex === 'number' ? data[activeIndex] : undefined;

  const goBackHandler = () => {
    setActiveIndex(undefined);
    setOtherActiveIndex(undefined);
  };

  return (
    <>
      <TextContainer>
        <Typography
          color={colors.text.secondary}
          fontFamily="primaryMedium"
          type={titleType}
          fontWeight={500}
        >
          {!selectedAsset
            ? t('wallet_pie_title')
            : t('wallet_my_asset_data', { name: selectedAsset.name })}
        </Typography>
      </TextContainer>
      <PieChartWrapper>
        <PieChartContainer>
          <PieChart
            data={otherChartData}
            onChartItemPress={setOtherActiveIndex}
            hasActiveSelection={activeIndex !== undefined}
          >
            <PieChartDetails
              totalAssets={totalAssetCount}
              selectedAsset={selectedAsset}
              currencySymbol={currencySymbol}
              activeDurationKey={activeDurationKey}
            />
          </PieChart>
        </PieChartContainer>
      </PieChartWrapper>

      <BackButtonWrapper onClick={goBackHandler}>
        <CustomDropDownIcon color={colors.interactive.brandColor} />
        <Typography
          type="headline"
          fontFamily="primaryMedium"
          color={colors.interactive.brandColor}
        >
          {t('common_back')}
        </Typography>
      </BackButtonWrapper>
    </>
  );
};
export default PieChartOtherData;
